import { useMemo, useState, ReactNode, useCallback } from 'react';

import { NavigationConfirmDialog } from 'src/components/custom-dialog';

import { NavigationDialogContext, NavigationDialogContextProps } from './navigation-context';

// ----------------------------------------------------------------------

type NavigationDialogProviderProps = {
  children: ReactNode;
};

export function NavigationDialogProvider({ children }: NavigationDialogProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState<string | undefined>();
  const [content, setContent] = useState<string | undefined>();
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const [resolvePromise, setResolvePromise] = useState<((value: boolean) => void) | null>(null);

  const showConfirmDialog = useCallback(
    (dialogTitle?: string, dialogContent?: string): Promise<boolean> =>
      new Promise((resolve) => {
        setTitle(dialogTitle);
        setContent(dialogContent);
        setIsOpen(true);
        setResolvePromise(() => resolve);
      }),
    []
  );

  const closeDialog = useCallback(() => {
    setIsOpen(false);
    setTitle(undefined);
    setContent(undefined);
    setPendingNavigation(null);
    setResolvePromise(null);
  }, []);

  const confirmNavigation = useCallback(() => {
    if (resolvePromise) {
      resolvePromise(true);
    }
    if (pendingNavigation) {
      pendingNavigation();
    }
    closeDialog();
  }, [resolvePromise, pendingNavigation, closeDialog]);

  const cancelNavigation = useCallback(() => {
    if (resolvePromise) {
      resolvePromise(false);
    }
    closeDialog();
  }, [resolvePromise, closeDialog]);

  const contextValue: NavigationDialogContextProps = useMemo(
    () => ({
      isOpen,
      title,
      content,
      pendingNavigation,
      showConfirmDialog,
      closeDialog,
      confirmNavigation,
      cancelNavigation,
    }),
    [isOpen, title, content, pendingNavigation, showConfirmDialog, closeDialog, confirmNavigation, cancelNavigation]
  );

  return (
    <NavigationDialogContext.Provider value={contextValue}>
      {children}
      <NavigationConfirmDialog
        open={isOpen}
        title={title}
        content={content}
        onConfirm={confirmNavigation}
        onCancel={cancelNavigation}
      />
    </NavigationDialogContext.Provider>
  );
}